// JS-1086 (hi-fi Desk): tiny shared presentational helpers so
// MultiStrategyScreenTable and EditionPage map the SAME semantic tone to the
// SAME Tailwind class for every threshold/vol-read treatment the hi-fi design
// spec (desk-hifi/design-spec.md §2) calls for -- never two components
// independently inventing their own coloring for the same fact. Every map
// here is presentation-only: the underlying tone/word facts still come from
// screenerLabels.ts's pure functions (spreadLiquiditySignifier,
// beAdrSignifier, volRegimeTone); this module only says how each tone paints.

import type { BeAdrTone } from "@/domains/trade-ideas/lib/screenerLabels";
import type { LiquidityTone } from "@/domains/trade-ideas/lib/screenerLabels";
import type { VolRegimeTone } from "@/domains/trade-ideas/lib/screenerLabels";

// Liquidity tone (Sprd% and any other spread-width-derived fact) -> Tailwind
// class. Matches the row's own gain/warning/loss semantic tokens: neutral
// text, amber warning, red flag.
export const LIQUIDITY_TONE_CLASS: Record<LiquidityTone, string> = {
  neutral: "text-foreground",
  warning: "text-warning",
  flag: "text-loss",
};

// JS-1088: BE×ADR tone (breakeven distance as a multiple of average daily
// range) -> Tailwind class -- the SAME three-tier neutral/warning/loss
// mapping spreadLiquiditySignifier's own tone uses one column over (ok/warn/
// flag mirror neutral/warning/flag exactly), so the two threshold columns
// read with identical visual weight for the same severity.
export const BE_ADR_TONE_CLASS: Record<BeAdrTone, string> = {
  ok: "text-foreground",
  warn: "text-warning",
  flag: "text-loss",
};

// Vol-read tone -- cheap reuses the SAME --gain hue "bullish/confirms"
// already means elsewhere (buying premium into a cheap reading is the
// constructive case), rich reuses the SAME --warning hue the threshold cells
// use (a caution-adjacent fact worth a second look), balanced is plain
// foreground (no signal), and an unrecognized/unknown regime is muted italic
// (matches this app's other honest-absence conventions -- never fabricated
// as "balanced").
export const VOL_REGIME_TONE_CLASS: Record<VolRegimeTone, string> = {
  cheap: "text-gain",
  rich: "text-warning",
  balanced: "text-foreground",
  unknown: "italic text-muted-foreground",
};
